import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { useCopilotStore } from "@/store/copilot.store"
import { cn } from "@/lib/utils"
import { GirlAvatar } from "./GirlAvatar"

export function FloatingAIPill() {
  const toggleOpen = useCopilotStore((s) => s.toggleOpen)
  const isOpen = useCopilotStore((s) => s.isOpen)
  const unreadCount = useCopilotStore((s) => s.unreadCount)

  // Track keyboard height on iOS where the layout viewport does not resize
  const [keyboardOffset, setKeyboardOffset] = useState(0)

  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return

    const vv = window.visualViewport
    const updateOffset = () => {
      // Calculate how much the virtual keyboard overlaps the layout viewport
      const rawOffset = window.innerHeight - (vv.offsetTop + vv.height)
      setKeyboardOffset(rawOffset > 50 ? rawOffset : 0)
    }

    vv.addEventListener("resize", updateOffset)
    vv.addEventListener("scroll", updateOffset)

    return () => {
      vv.removeEventListener("resize", updateOffset)
      vv.removeEventListener("scroll", updateOffset)
    }
  }, [])

  return (
    <motion.button
      onClick={toggleOpen}
      className={cn(
        "group fixed right-6 z-[30] size-14 items-center justify-center rounded-full cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-[#be001e] focus:ring-offset-2",
        isOpen ? "hidden sm:flex" : "flex"
      )}
      style={{
        bottom: `${24 + keyboardOffset}px`,
        transition: "bottom 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ scale: { type: "spring", stiffness: 260, damping: 20 }, opacity: { duration: 0.2 } }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.94 }}
      aria-label={isOpen ? "Close Enga AI assistant" : "Open Enga AI assistant"}
    >
      {/* Pulsing ring while idle */}
      {!isOpen && <span className="absolute inset-0 rounded-full bg-[#be001e]/25 riya-ring" />}

      {/* Gradient ring */}
      <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#f07a2e] via-[#be001e] to-[#e6202f] shadow-[0_0_20px_rgba(190,0,30,0.55)] ring-2 ring-white/80" />

      {/* White inner disc with the girl */}
      <span className="relative z-10 flex size-[3.2rem] items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-white to-[#fff0ed] ring-1 ring-[#be001e]/20">
        <span className={cn("riya-float", isOpen && "hidden")}>
          <GirlAvatar size={40} talking={!isOpen} />
        </span>
        {isOpen && (
          <span className="flex size-full items-center justify-center">
            <ChevronDown className="size-5 text-[#be001e]" />
          </span>
        )}
      </span>

      {/* Unread badge */}
      {!isOpen && unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 z-20 flex size-5 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-black text-white border-2 border-white animate-bounce">
          {unreadCount}
        </span>
      )}
    </motion.button>
  )
}